import type { AxiosError } from 'axios';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { ArrowLeft, Edit3 } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  type TextInput,
  type TextInputProps,
  useWindowDimensions,
} from 'react-native';
import { FadeIn } from '../../../../../src/components/animations';
import { Box, HStack, Text, VStack } from '../../../../../src/components/primitives';
import { Button } from '../../../../../src/components/primitives/Button';
import { FormInput } from '../../../../../src/components/primitives/FormInput';
import { PhoneNumberInput } from '../../../../../src/components/primitives/PhoneNumberInput';
import { useAuth } from '../../../../../src/contexts/AuthContext';
import { useToast } from '../../../../../src/contexts/ToastContext';
import { useTranslation } from '../../../../../src/contexts/TranslationContext';
import { AssigneePicker } from '../../../../../src/features/leads/components/AssigneePicker';
import { buildLeadUpdatePayload } from '../../../../../src/features/leads/utils/buildLeadUpdatePayload';
import { useLead, useUpdateLead } from '../../../../../src/hooks/useLeads';
import { useUnsavedChangesGuard } from '../../../../../src/hooks/useUnsavedChangesGuard';
import { colors } from '../../../../../src/theme/colors';
import { tokens } from '../../../../../src/theme/tokens';
import { isValidLeadPhone } from '../../../../../src/utils/phone';

interface ValidationErrorResponse {
  message: string | string[];
  statusCode: number;
}

function readCompanyProfile(
  customFields: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const profile = customFields?.companyProfile;
  return profile && typeof profile === 'object' ? (profile as Record<string, unknown>) : {};
}

function readUrl(profile: Record<string, unknown>, key: string): string {
  const value = profile[key];
  return typeof value === 'string' ? value : '';
}

interface FormFieldProps {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  keyboardType?: TextInputProps['keyboardType'];
  required?: boolean;
  multiline?: boolean;
  numberOfLines?: number;
  testID?: string;
}

function FormField({
  label,
  value,
  onChangeText,
  placeholder,
  keyboardType = 'default',
  required,
  multiline = false,
  numberOfLines,
  testID,
}: Readonly<FormFieldProps>) {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<TextInput>(null);

  const inputShellStyle = [
    styles.inputShell,
    isHovered && styles.inputShellHovered,
    isFocused && styles.inputShellFocused,
    multiline && styles.inputShellMultiline,
  ];

  return (
    <VStack spacing="xs">
      <Text variant="caption" color={colors.text.secondary} style={styles.fieldLabel}>
        {label}
        {required && (
          <Text variant="caption" color={colors.status.error.main}>
            {' '}
            *
          </Text>
        )}
      </Text>
      <Pressable
        accessible={false}
        style={inputShellStyle}
        onPress={() => inputRef.current?.focus()}
        onPointerEnter={Platform.OS === 'web' ? () => setIsHovered(true) : undefined}
        onPointerLeave={Platform.OS === 'web' ? () => setIsHovered(false) : undefined}
      >
        <FormInput
          ref={inputRef}
          testID={testID}
          accessibilityLabel={label}
          accessibilityValue={value ? { text: value } : undefined}
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          keyboardType={keyboardType}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          multiline={multiline}
          numberOfLines={numberOfLines}
          autoCapitalize={
            keyboardType === 'email-address' || keyboardType === 'url' ? 'none' : 'words'
          }
          autoCorrect={false}
          style={[styles.input, multiline && styles.inputMultiline]}
        />
      </Pressable>
    </VStack>
  );
}

export default function EditLeadScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading, currentOrganization } = useAuth();
  const { t } = useTranslation();
  const { showSuccess } = useToast();
  const { width } = useWindowDimensions();
  const isDesktop = width >= tokens.breakpoints.desktop;

  const orgId = currentOrganization?.id;
  const { data: lead, isLoading: leadLoading } = useLead(id, orgId);
  const updateLead = useUpdateLead();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [companyWebsiteUrl, setCompanyWebsiteUrl] = useState('');
  const [companyLinkedinUrl, setCompanyLinkedinUrl] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [tags, setTags] = useState('');
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [formError, setFormError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [savedSuccessfully, setSavedSuccessfully] = useState(false);

  useEffect(() => {
    if (lead && !initialized) {
      const companyProfile = readCompanyProfile(lead.customFields);
      setFullName(lead.fullName ?? '');
      setEmail(lead.email ?? '');
      setPhone(lead.phone ?? '');
      setCompany(lead.company ?? '');
      setCompanyWebsiteUrl(readUrl(companyProfile, 'companyUrl'));
      setCompanyLinkedinUrl(readUrl(companyProfile, 'companyLinkedinUrl'));
      setJobTitle(lead.jobTitle ?? '');
      setTags(lead.tags?.join(', ') ?? '');
      setAssignedTo(lead.assignedTo ?? null);
      setInitialized(true);
    }
  }, [lead, initialized]);

  const initialCompanyProfile = readCompanyProfile(lead?.customFields);

  useUnsavedChangesGuard(
    initialized &&
      !savedSuccessfully &&
      Boolean(lead) &&
      (fullName !== (lead?.fullName ?? '') ||
        email !== (lead?.email ?? '') ||
        phone !== (lead?.phone ?? '') ||
        company !== (lead?.company ?? '') ||
        companyWebsiteUrl !== readUrl(initialCompanyProfile, 'companyUrl') ||
        companyLinkedinUrl !== readUrl(initialCompanyProfile, 'companyLinkedinUrl') ||
        jobTitle !== (lead?.jobTitle ?? '') ||
        tags !== (lead?.tags?.join(', ') ?? '') ||
        assignedTo !== (lead?.assignedTo ?? null)),
  );

  const handleUpdate = useCallback(async () => {
    if (!id) return;
    setFormError('');

    if (!company && !fullName) {
      setFormError(t('leads.new.companyOrContactRequired'));
      return;
    }

    // Only enforce the country-code rule on a phone the user actually changed,
    // so editing an unrelated field on a lead with a legacy (pre-E.164) phone
    // is never blocked by it.
    const phoneChanged = phone.trim() !== (lead?.phone ?? '').trim();
    if (phone.trim() && phoneChanged && !isValidLeadPhone(phone)) {
      setPhoneError(t('leads.new.phoneInvalid'));
      return;
    }
    setPhoneError('');

    try {
      await updateLead.mutateAsync({
        id,
        payload: buildLeadUpdatePayload(
          {
            fullName,
            email,
            phone,
            company,
            jobTitle,
            companyWebsiteUrl,
            companyLinkedinUrl,
            tags,
            assignedTo,
          },
          {
            email: lead?.email ?? '',
            phone: lead?.phone ?? '',
            company: lead?.company ?? '',
            jobTitle: lead?.jobTitle ?? '',
            customFields: lead?.customFields,
          },
        ),
      });

      setSavedSuccessfully(true);
      showSuccess(t('leads.edit.successToast'));
      router.canGoBack() ? router.back() : router.replace(`/lead/${id}`);
    } catch (error) {
      const axiosError = error as AxiosError<ValidationErrorResponse>;
      const responseData = axiosError.response?.data;
      if (responseData?.message) {
        const messages = Array.isArray(responseData.message)
          ? responseData.message
          : [responseData.message];
        setFormError(messages.join('. '));
      } else {
        setFormError(t('leads.edit.updateError'));
      }
    }
  }, [
    assignedTo,
    company,
    companyLinkedinUrl,
    companyWebsiteUrl,
    email,
    fullName,
    id,
    jobTitle,
    lead,
    phone,
    router,
    showSuccess,
    t,
    tags,
    updateLead,
  ]);

  if (authLoading || leadLoading) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center">
        <ActivityIndicator size="large" color={colors.primary[500]} />
      </Box>
    );
  }

  if (!isAuthenticated || !lead) return null;

  const canSubmit = Boolean(company || fullName);

  return (
    <ScrollView
      testID="edit_lead_screen"
      style={styles.scrollView}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <Box style={[styles.formContainer, isDesktop && styles.formContainerDesktop]}>
        <FadeIn delay={0} translateY={12}>
          <VStack spacing="lg">
            <HStack spacing="md" align="center">
              <Pressable
                testID="edit_lead_back_button"
                onPress={() => (router.canGoBack() ? router.back() : router.replace(`/lead/${id}`))}
                accessibilityRole="button"
              >
                <Box
                  width={38}
                  height={38}
                  borderRadius="md"
                  alignItems="center"
                  justifyContent="center"
                  style={styles.backButton}
                >
                  <ArrowLeft size={18} color={colors.text.secondary} strokeWidth={2} />
                </Box>
              </Pressable>
              <VStack spacing="none">
                <HStack spacing="xs" align="center">
                  <Edit3 size={14} color={colors.primary.main} strokeWidth={2} />
                  <Text variant="caption" color={colors.primary.main} style={styles.editBadge}>
                    {t('leads.edit.title')}
                  </Text>
                </HStack>
                <Text variant="h4" color={colors.text.primary} style={styles.title}>
                  {t('leads.edit.subtitle')}
                </Text>
              </VStack>
            </HStack>

            <Box style={styles.formCard}>
              <VStack spacing="lg">
                <Text variant="h6" color={colors.text.primary} style={styles.sectionTitle}>
                  {t('leads.new.contactInfo')}
                </Text>

                <FormField
                  label={t('leads.new.fullName')}
                  value={fullName}
                  onChangeText={setFullName}
                  placeholder={t('leads.new.fullNamePlaceholder')}
                  testID="edit_lead_fullname_input"
                />
                <FormField
                  label={t('leads.new.emailAddress')}
                  value={email}
                  onChangeText={setEmail}
                  placeholder={t('leads.new.emailPlaceholder')}
                  keyboardType="email-address"
                  testID="edit_lead_email_input"
                />
                <PhoneNumberInput
                  label={t('leads.new.phoneNumber')}
                  value={phone}
                  onChangeText={(text) => {
                    setPhone(text);
                    if (phoneError) setPhoneError('');
                  }}
                  placeholder={t('leads.new.phonePlaceholder')}
                  error={phoneError}
                  testID="edit_lead_phone_input"
                  searchPlaceholder={t('leads.new.phoneCountrySearch')}
                  modalTitle={t('leads.new.phoneCountryTitle')}
                />
                <FormField
                  label={t('leads.new.company')}
                  value={company}
                  onChangeText={setCompany}
                  placeholder={t('leads.new.companyPlaceholder')}
                  testID="edit_lead_company_input"
                />
                <Text variant="caption" color={colors.text.tertiary}>
                  {t('leads.new.companyOrContactRequired')}
                </Text>
                <FormField
                  label={t('leads.new.companyWebsiteUrl')}
                  value={companyWebsiteUrl}
                  onChangeText={setCompanyWebsiteUrl}
                  placeholder={t('leads.new.companyWebsiteUrlPlaceholder')}
                  keyboardType="url"
                  testID="edit_lead_company_website_input"
                />
                <FormField
                  label={t('leads.new.companyLinkedinUrl')}
                  value={companyLinkedinUrl}
                  onChangeText={setCompanyLinkedinUrl}
                  placeholder={t('leads.new.companyLinkedinUrlPlaceholder')}
                  keyboardType="url"
                  testID="edit_lead_company_linkedin_input"
                />
                <FormField
                  label={t('leads.new.jobTitle')}
                  value={jobTitle}
                  onChangeText={setJobTitle}
                  placeholder={t('leads.new.jobTitlePlaceholder')}
                  testID="edit_lead_jobtitle_input"
                />

                <FormField
                  label={t('leads.new.tags')}
                  value={tags}
                  onChangeText={setTags}
                  placeholder={t('leads.new.tagsPlaceholder')}
                  testID="edit_lead_tags_input"
                />

                {orgId && (
                  <VStack spacing="xs" style={{ zIndex: 20, position: 'relative' }}>
                    <Text variant="caption" color={colors.text.secondary} style={styles.fieldLabel}>
                      {t('leads.detail.assignedTo')}
                    </Text>
                    <AssigneePicker
                      organizationId={orgId}
                      value={assignedTo}
                      onChange={setAssignedTo}
                    />
                  </VStack>
                )}

                {(updateLead.isError || Boolean(formError)) && (
                  <Box backgroundColor={colors.status.error.bg} borderRadius="md" padding="md">
                    <Text variant="bodySmall" color={colors.status.error.main}>
                      {formError || t('leads.edit.updateError')}
                    </Text>
                  </Box>
                )}

                <HStack spacing="md" justify="end" style={styles.actionRow}>
                  <Button
                    testID="edit_lead_cancel_button"
                    variant="ghost"
                    size="md"
                    onPress={() =>
                      router.canGoBack() ? router.back() : router.replace(`/lead/${id}`)
                    }
                  >
                    {t('common.actions.cancel')}
                  </Button>
                  <Button
                    testID="edit_lead_submit_button"
                    variant="primary"
                    size="md"
                    onPress={handleUpdate}
                    loading={updateLead.isPending}
                    disabled={!canSubmit}
                  >
                    {t('common.actions.saveChanges')}
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </VStack>
        </FadeIn>
      </Box>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: tokens.spacing['3xl'],
  },
  formContainer: {
    width: '100%',
    maxWidth: 680,
    alignSelf: 'center',
  },
  formContainerDesktop: {
    maxWidth: 760,
  },
  backButton: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.cardOverlay,
  },
  editBadge: {
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    fontWeight: '700',
  },
  title: {
    fontWeight: '700',
    letterSpacing: -0.4,
  },
  formCard: {
    borderRadius: tokens.radius.xl,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.cardOverlay,
    padding: tokens.spacing.lg,
  },
  sectionTitle: {
    fontWeight: '600',
  },
  fieldLabel: {
    fontWeight: '500',
    letterSpacing: 0.3,
  },
  inputShell: {
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.input,
    backgroundColor: colors.background.input,
    paddingHorizontal: tokens.spacing.md,
    minHeight: 48,
    justifyContent: 'center',
  },
  inputShellHovered: {
    borderColor: colors.border.default,
    backgroundColor: colors.background.elevated,
  },
  inputShellFocused: {
    borderColor: colors.primary.main,
    backgroundColor: colors.background.elevated,
    transform: [{ translateY: -1 }],
  },
  inputShellMultiline: {
    minHeight: 92,
    justifyContent: 'flex-start',
    paddingTop: tokens.spacing.sm,
    paddingBottom: tokens.spacing.sm,
  },
  input: {
    width: '100%',
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    minHeight:
      Platform.OS === 'android'
        ? tokens.interaction.minimumTarget.android
        : tokens.interaction.minimumTarget.default,
  },
  inputMultiline: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  actionRow: {
    marginTop: tokens.spacing.sm,
  },
});
